import ErrorHandler from '../error/handling'
import { objectToSnakeCase } from '../json/case'
import { Logger } from '../logger/log'
import { isAdminUser } from '../user/auth'

export const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID

export const GA_NAMES = {
  TASK_COMPLETED: 'task_completed',
  ROI_REPORT_DATE_FILTER_CHANGED: 'roi_report_date_filter_changed',
  LOGIN_SUCCESS: 'login_success',
  CONTACT_US: 'contact_us',
  GOOGLE_DRIVE: 'google_drive',
  EMAIL_VERIFIED: 'email_verified',
  SMS_VERIFIED: 'sms_verified',
  MERCHANT_SETTINGS_UPDATED: 'merchant_settings_updated',
  CONTENT_PREVIEW_SIMULATION: 'content_preview_simulation',
  WEBSITE_CONTENT_REGENERATION: 'website_content_regeneration',
} as const

export const GA_ACTIONS = {
  APPROVE: 'approve',
  DISMISS: 'dismiss',
  REJECT: 'reject',
  FILTER: 'filter',
  SUCCESS: 'success',
  CLICK: 'click',
  UPLOAD: 'upload',
  GENERATE: 'generate',
  REGENERATE: 'regenerate',
  REFINE: 'refine',
} as const

export const GA_CATEGORIES = {
  TASK: 'task',
  REPORT: 'report',
  AUTH: 'auth',
  SUPPORT: 'support',
  FILE: 'file',
  SETTINGS: 'settings',
  SIMULATION: 'simulation',
  WEBSITE: 'website',
} as const

export const GA_LABELS = {
  EMAIL: 'email',
  SMS: 'sms',
  LIVE_CHAT: 'live_chat',
  JOSUE_CALENDLY: 'josue_calendly',
} as const

interface GtagEvent {
  name: string
  action: string
  category: string
  label?: string
  value?: number
  customParameters?: Record<string, unknown>
}
export function isGaEnabled(): boolean {
  return !!(GA_MEASUREMENT_ID && typeof window !== 'undefined' && window.gtag)
}

export function sendGaCustomEvent(event: GtagEvent): void {
  const payload = objectToSnakeCase({
    name: event.name,
    action: event.action,
    category: event.category,
    label: event.label || '',
    value: event.value || undefined,
    ...(event.customParameters || {}),
  })

  if (!isGaEnabled()) {
    Logger.info('GA not enabled. Event would be sent:', payload)
    return
  }

  try {
    window.gtag('event', event.name, payload)
    Logger.info('GA Event sent:', payload)
  } catch (error) {
    ErrorHandler.handle(error as Error)
  }
}

export function trackTaskApproval(task: Task): void {
  const event: GtagEvent = {
    name: GA_NAMES.TASK_COMPLETED,
    action: GA_ACTIONS.APPROVE,
    category: GA_CATEGORIES.TASK,
    label: task.type,
    customParameters: {
      task_id: task.id,
      business_id: task.businessId,
    },
  }

  sendGaCustomEvent(event)
}

export function trackTaskDismiss(task: Task): void {
  const event: GtagEvent = {
    name: GA_NAMES.TASK_COMPLETED,
    action: GA_ACTIONS.DISMISS,
    category: GA_CATEGORIES.TASK,
    label: task.type,
    customParameters: {
      task_id: task.id,
      business_id: task.businessId,
    },
  }

  sendGaCustomEvent(event)
}

export function trackTaskReject(task: Task): void {
  const event: GtagEvent = {
    name: GA_NAMES.TASK_COMPLETED,
    action: GA_ACTIONS.REJECT,
    category: GA_CATEGORIES.TASK,
    label: task.type,
    customParameters: {
      task_id: task.id,
      business_id: task.businessId,
    },
  }

  sendGaCustomEvent(event)
}

export function trackRoiReportDateFilterChange(): void {
  const event: GtagEvent = {
    name: GA_NAMES.ROI_REPORT_DATE_FILTER_CHANGED,
    action: GA_ACTIONS.FILTER,
    category: GA_CATEGORIES.REPORT,
  }

  sendGaCustomEvent(event)
}

export function trackLoginSuccess(userAuth: UserAuth): void {
  if (isAdminUser(userAuth)) {
    // we don't want to track admin login success
    return
  }

  const { user } = userAuth || {}
  const { id: userId, email } = user || {}

  const event: GtagEvent = {
    name: GA_NAMES.LOGIN_SUCCESS,
    action: GA_ACTIONS.SUCCESS,
    category: GA_CATEGORIES.AUTH,
    customParameters: {
      user_id: userId,
      user_email: email,
    },
  }

  sendGaCustomEvent(event)
}

export function trackContactUsEmail(): void {
  const event: GtagEvent = {
    name: GA_NAMES.CONTACT_US,
    action: GA_ACTIONS.CLICK,
    category: GA_CATEGORIES.SUPPORT,
    label: GA_LABELS.EMAIL,
  }

  sendGaCustomEvent(event)
}

export function trackContactUsLiveChat(): void {
  const event: GtagEvent = {
    name: GA_NAMES.CONTACT_US,
    action: GA_ACTIONS.CLICK,
    category: GA_CATEGORIES.SUPPORT,
    label: GA_LABELS.LIVE_CHAT,
  }

  sendGaCustomEvent(event)
}

export function trackContactUsSms(): void {
  const event: GtagEvent = {
    name: GA_NAMES.CONTACT_US,
    action: GA_ACTIONS.CLICK,
    category: GA_CATEGORIES.SUPPORT,
    label: GA_LABELS.SMS,
  }

  sendGaCustomEvent(event)
}

export function trackContactUsJosueCalendly(): void {
  const event: GtagEvent = {
    name: GA_NAMES.CONTACT_US,
    action: GA_ACTIONS.CLICK,
    category: GA_CATEGORIES.SUPPORT,
    label: GA_LABELS.JOSUE_CALENDLY,
  }

  sendGaCustomEvent(event)
}

export function trackGoogleDriveUploadCompleted(fileCount: number): void {
  const event: GtagEvent = {
    name: GA_NAMES.GOOGLE_DRIVE,
    action: GA_ACTIONS.UPLOAD,
    category: GA_CATEGORIES.FILE,
    value: fileCount,
  }

  sendGaCustomEvent(event)
}

export function trackEmailVerified(userId?: number, email?: string): void {
  const event: GtagEvent = {
    name: GA_NAMES.EMAIL_VERIFIED,
    action: GA_ACTIONS.SUCCESS,
    category: GA_CATEGORIES.AUTH,
    label: GA_LABELS.EMAIL,
    customParameters: {
      user_id: userId,
      user_email: email,
    },
  }

  sendGaCustomEvent(event)
}

export function trackSmsVerified(userId?: number, phoneNumber?: string): void {
  const event: GtagEvent = {
    name: GA_NAMES.SMS_VERIFIED,
    action: GA_ACTIONS.SUCCESS,
    category: GA_CATEGORIES.AUTH,
    label: GA_LABELS.SMS,
    customParameters: {
      user_id: userId,
      phone_number: phoneNumber,
    },
  }

  sendGaCustomEvent(event)
}

export function trackMerchantSettingsUpdated(section?: string): void {
  const event: GtagEvent = {
    name: GA_NAMES.MERCHANT_SETTINGS_UPDATED,
    action: GA_ACTIONS.SUCCESS,
    category: GA_CATEGORIES.SETTINGS,
    label: section,
    customParameters: section ? { section } : undefined,
  }

  sendGaCustomEvent(event)
}

export function trackAiPreviewGenerate(contextId: number): void {
  sendGaCustomEvent({
    name: GA_NAMES.CONTENT_PREVIEW_SIMULATION,
    action: GA_ACTIONS.GENERATE,
    category: GA_CATEGORIES.SIMULATION,
    customParameters: { context_id: contextId },
  })
}

export function trackAiPreviewApprove(contextId: number, simulationId: number): void {
  sendGaCustomEvent({
    name: GA_NAMES.CONTENT_PREVIEW_SIMULATION,
    action: GA_ACTIONS.APPROVE,
    category: GA_CATEGORIES.SIMULATION,
    customParameters: { context_id: contextId, simulation_id: simulationId },
  })
}

export function trackAiPreviewRefine(contextId: number, simulationId: number): void {
  sendGaCustomEvent({
    name: GA_NAMES.CONTENT_PREVIEW_SIMULATION,
    action: GA_ACTIONS.REFINE,
    category: GA_CATEGORIES.SIMULATION,
    customParameters: { context_id: contextId, simulation_id: simulationId },
  })
}

export function trackWebsiteContentRegeneration(websiteId: number): void {
  sendGaCustomEvent({
    name: GA_NAMES.WEBSITE_CONTENT_REGENERATION,
    action: GA_ACTIONS.REGENERATE,
    category: GA_CATEGORIES.WEBSITE,
    customParameters: { website_id: websiteId },
  })
}

declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js',
      targetId: string | Date,
      config?: Record<string, unknown>
    ) => void
    dataLayer: unknown[]
  }
}
